import React,  { useState, useContext, useEffect } from 'react';
import Modal from 'react-modal';
import { DeparmentContext } from '../context/DepartmentContext';
import { PropertyContext } from '../context/PropertyContext';
import { AiOutlineClose } from 'react-icons/ai';
import "./TicketsScreenModal.css";
import RowItem from '../components/RowItem';
import { Dialog, DialogTitle, List, ListItem, ListItemText, Chip, Box, Typography } from '@mui/material';
import useTicketStatusParser from '../utils/ticketUtils';
import Button from '../components/Button';
import { useTranslation } from 'react-i18next';

const TicketsScreenModal = ({ isOpen, onRequestClose }) => {

  const { t } = useTranslation();
  const { parseTicketStatus } = useTicketStatusParser();
  const { departments } = useContext(DeparmentContext);
  const { properties } = useContext(PropertyContext);

  const [isPropertyDialogOpen, setIsPropertyDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(30);

  const [isDepartmentsDialogOpen, setIsDepartmentsDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [isStatusesDialogOpen, setIsStatusesDialogOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(['OPEN', 'IN PROGRESS', 'CLOSED', 'ON HOLD']);

  const sortDirections = ['asc', 'desc'];
  const sortDirectionLabels = {
    asc: t('ticketFilter.sortAsc'),
    desc: t('ticketFilter.sortDesc')
  };

  const sortFields = [
    { value: 'number', label: t('ticketFilter.sortNumber') },
    { value: 'property', label: t('ticketFilter.sortProperty') },
    { value: 'assignee', label: t('ticketFilter.sortAssignee') },
    { value: 'status', label: t('ticketFilter.sortStatus') },
    { value: 'priority', label: t('ticketFilter.sortPriority') },
    { value: 'date', label: t('ticketFilter.sortDate') },
    { value: 'title', label: t('ticketFilter.sortTitle') }
  ];

  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('date');

  const priorityOptions = [
    { value: 'Low', label: t('ticketPriority.low') },
    { value: 'Medium', label: t('ticketPriority.medium') },
    { value: 'High', label: t('ticketPriority.high') },
    { value: 'CRITICAL', label: t('ticketPriority.critical') }
  ];

  const [selectedPriorities, setSelectedPriorities] = useState(priorityOptions.map(opt => opt.value));


  useEffect(() => {
    getFilterFromStorage();
  }, []);

  const handlePropertyClick = () => {
    setIsPropertyDialogOpen(true);
  };

  useEffect(() => {
    saveFilterInLocalStorage({
      sortDirection: sortDirection,
      sortField: sortField
    });
  }, [sortDirection, sortField]);


  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setIsPropertyDialogOpen(false);
    saveFilterInLocalStorage({
      property: property
    });
  };

  const handleDateRangeClick = () => {
    setIsDateRangeDialogOpen(true);
  };

  const handleDateRangeSelect = (range) => {
    setSelectedDateRange(range);
    setIsDateRangeDialogOpen(false);
    saveFilterInLocalStorage({
      dateRange: range
    });
  };

  const handleDepartmentsClick = () => {
    setIsDepartmentsDialogOpen(true);
  }

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
    setIsDepartmentsDialogOpen(false);
    saveFilterInLocalStorage({
      department: department
    });
  }

  const handleStatusesClick = () => {
    setIsStatusesDialogOpen(true);
  }

  const handleStatusSelect = (status) => {
    const currentStatuses = [...selectedStatuses];
    const index = currentStatuses.indexOf(status);
    if (index > -1) {
      currentStatuses.splice(index, 1);
    } else {
      currentStatuses.push(status);
    }
    setSelectedStatuses(currentStatuses);
    saveFilterInLocalStorage({
      statuses: currentStatuses
    });
  }

  const handleResetClick = () => {
    setSelectedDateRange(30);
    setSelectedDepartment(null);
    setSelectedStatuses(['OPEN', 'IN PROGRESS', 'CLOSED', 'ON HOLD']);
    setSelectedProperty(null);
    setSortDirection('desc');
    setSortField('number');
    setSelectedPriorities(priorityOptions.map(opt => opt.value));
    saveFilterInLocalStorage({
      dateRange: 30,
      department: null,
      statuses: ['OPEN', 'IN PROGRESS', 'CLOSED', 'ON HOLD'],
      property: null,
      sortDirection: 'desc',
      sortField: 'number',
      priorities: priorityOptions.map(opt => opt.value)
    });
  }


  const saveFilterInLocalStorage = (params) => {
    localStorage.setItem('ticketFilter', JSON.stringify({
      dateRange: params.dateRange === undefined ? selectedDateRange : params.dateRange,
      department: params.department === undefined ? selectedDepartment : params.department,
      statuses: params.statuses === undefined ? selectedStatuses : params.statuses,
      property: params.property === undefined ? selectedProperty : params.property,
      sortDirection: params.sortDirection || sortDirection,
      sortField: params.sortField || sortField,
      priorities: params.priorities === undefined ? selectedPriorities : params.priorities
    }));
  }
  
  const getFilterFromStorage = () => {
    const filter = localStorage.getItem('ticketFilter') || "{}";
    const { dateRange, department, statuses, property, priorities } = JSON.parse(filter);
    setSelectedDateRange(dateRange || 30);
    setSelectedDepartment(department || null);
    setSelectedStatuses(statuses || ['OPEN', 'IN PROGRESS', 'CLOSED', 'ON HOLD']);
    setSelectedProperty(property || null);
    setSortDirection(localStorage.getItem('sortDirection') || 'desc');
    setSortField(localStorage.getItem('sortField') || 'number');
    setSelectedPriorities(priorities || priorityOptions.map(opt => opt.value));
  }
  
  const handleShowClick = () => {
    onRequestClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Filters Modal"
      style={{
        content: {
          width: '95vw',
          height: '95vh',
          padding: '8px',
          paddingLeft: 0,
          paddingRight: 0,
          maxWidth: '100vw',
          maxHeight: '100vh',
          margin: '0 auto',
          left: 0,
          right: 0,
          boxSizing: 'border-box',
          border: '1px solid #ddd', // restore subtle border
          borderRadius: '10px', // optional: add some rounding
          background: '#fff', // ensure background is white
        }
      }}
    >
      <div className="modal-header">
        <h3>{t("ticketFilter.title")}</h3>
        <button className="close-button" onClick={onRequestClose}>
          <AiOutlineClose size={24} />
        </button>
      </div>
      <div className='modal-content'>
      <RowItem 
        title={t("ticketFilter.time")}
        details={t("ticketFilter.days", {count: selectedDateRange})}
        onClick={handleDateRangeClick} 
        showDetailsIndicator={true}/>

      <RowItem
        title={t("ticketFilter.department")}
        details={selectedDepartment ? selectedDepartment.name : t('general.all')}
        onClick={handleDepartmentsClick}
        showDetailsIndicator={true}/>

      <RowItem  
        title={t("ticketFilter.property")}
        details={selectedProperty ? selectedProperty.name : t('general.all')}
        onClick={handlePropertyClick}
        showDetailsIndicator={true}/>

      <RowItem
        title={t("ticketFilter.statuses")}
        details={
          selectedStatuses.length > 0 ? 
              selectedStatuses.map(parseTicketStatus).join(', ') : 
              t("general.all")
        }
        onClick={handleStatusesClick}
        showDetailsIndicator={true}/>

        {/* Priority Section */}
        <Box mt={4} mb={2} mx={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('ticketFilter.sortPriority')}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
            {priorityOptions.map(option => (
              <Chip
                key={option.value}
                label={option.label}
                color={selectedPriorities.includes(option.value) ? 'primary' : 'default'}
                variant={selectedPriorities.includes(option.value) ? 'filled' : 'outlined'}
                onClick={() => {
                  setSelectedPriorities(prev => {
                    if (prev.includes(option.value)) {
                      const updated = prev.filter(p => p !== option.value);
                      saveFilterInLocalStorage({ priorities: updated });
                      return updated;
                    } else {
                      const updated = [...prev, option.value];
                      saveFilterInLocalStorage({ priorities: updated });
                      return updated;
                    }
                  });
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Sorting Section */}
        <Box mt={4} mb={2} mx={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('ticketFilter.sorting')}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            {sortDirections.map(dir => (
              <Chip
                key={dir}
                label={sortDirectionLabels[dir]}
                color={sortDirection === dir ? 'primary' : 'default'}
                variant={sortDirection === dir ? 'filled' : 'outlined'}
                onClick={() => setSortDirection(dir)}
                sx={{ mr: 1 }}
              />
            ))}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
            {sortFields.map(field => (
              <Chip
                key={field.value}
                label={field.label}
                color={sortField === field.value ? 'primary' : 'default'}
                variant={sortField === field.value ? 'filled' : 'outlined'}
                onClick={() => setSortField(field.value)}
              />
            ))}
          </Box>
        </Box>

        <Button id="reset-button" variant="secondary" onClick={handleResetClick}>
          {t('ticketFilter.reset')}
        </Button>
        <Button id="show-button" variant="primary" onClick={handleShowClick}>
          {t('ticketFilter.show')}
        </Button>
      </div>
      <Dialog open={isPropertyDialogOpen} onClose={() => setIsPropertyDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{t("ticketFilter.selectProperty")}</DialogTitle>
        <List>
          <ListItem button key="all" onClick={() => handlePropertySelect(null)}>
            <ListItemText primary={t("general.all")} />
          </ListItem>
          {
            properties.map((property) => (
              <ListItem button key={property.id} onClick={() => handlePropertySelect(property)}>
                <ListItemText primary={property.name} />
              </ListItem>
            ))
          }
        </List>
      </Dialog>
      <Dialog open={isStatusesDialogOpen} onClose={() => setIsStatusesDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{t("ticketFilter.selectStatuses")}</DialogTitle>
        <List>
          {['OPEN', 'IN PROGRESS', 'CLOSED', 'ON HOLD'].map((status) => (
            <ListItem button key={status} onClick={() => handleStatusSelect(status)}>
              <ListItemText primary={parseTicketStatus(status)} />
              <div className="status-checkbox">
                {selectedStatuses.includes(status) && '✓'}
              </div>
            </ListItem>
          ))}
        </List>
      </Dialog>
      <Dialog open={isDepartmentsDialogOpen} onClose={() => setIsDepartmentsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{t("ticketFilter.selectDepartment")}</DialogTitle>
        <List>
          <ListItem button key="all" onClick={() => handleDepartmentSelect(null)}>
            <ListItemText primary={t("general.all")} />
          </ListItem>
          {
            departments.map((department) => (
              <ListItem button key={department} onClick={() => handleDepartmentSelect(department)}>
                <ListItemText primary={department.name} />
              </ListItem>
            ))
          }
        </List>
      </Dialog>
      <Dialog open={isDateRangeDialogOpen}  onClose={() => setIsDateRangeDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>{t("ticketFilter.selectTime")}</DialogTitle>
          <List>
            {
              [1, 3, 5, 7, 30].map((range) => (
                <ListItem button key={range} onClick={() => handleDateRangeSelect(range)}>
                  <ListItemText primary={ t("ticketFilter.days", {count: range}) } />
                </ListItem>
              ))
            }
          </List>
      </Dialog>
    </Modal>
  );
};

export default TicketsScreenModal;